/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Retracked from 'js/app/retracked';
import API from 'js/lib/api';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { SvgArrowLeft } from '@coursera/coursera-ui/svg';

import Submit from 'bundles/authentication/components/Submit';
import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication/constants';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/authentication';

export type Props = {
  className?: string;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  email: string;
  magicLinkRedirect?: string;
};

export type State = {
  magicLinkSent: boolean;
};

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const api = API('', { type: 'rest' });

const styles = {
  container: css`
    display: grid;
    grid-gap: var(--cds-spacing-300);
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: 0 var(--cds-spacing-400);
  `,
  description: css`
    text-align: center;
    word-wrap: break-word;
    white-space: pre-line;
    line-height: 24px;
    font-family: 'Source Sans Pro', Arial, sans-serif;
  `,
  buttonContainer: css`
    display: grid;
    grid-gap: var(--cds-spacing-150);
    grid-template-columns: 1fr;
    margin: 0 auto;
  `,
  navigation: css`
    margin: var(--cds-spacing-150) auto 0;
    display: flex;
    text-align: center;
    font-size: 14px;
    color: #0056d2 !important;
    text-decoration: underline;
    font-family: 'Source Sans Pro', Arial, sans-serif;
  `,
  navigationIcon: css`
    margin-right: 0.6rem;
  `,
  section: css`
    &:nth-child(1) {
      -ms-grid-row: 1;
    }

    &:nth-child(2) {
      -ms-grid-row: 2;
    }

    &:nth-child(3) {
      -ms-grid-row: 3;
    }

    &:nth-child(4) {
      -ms-grid-row: 4;
    }
  `,
  title: css`
    color: #1f1f1f;
    font-family: 'Source Sans Pro', Arial, sans-serif;
    font-size: 24px;
    line-height: 30px;
    margin: 0;
    text-align: center;

    @media (min-width: 600px) {
      font-size: 34px;
      line-height: 42px;
    }
  `,
  submit: css`
    margin-top: var(--cds-spacing-150);
    margin-bottom: 20px;
    width: 200px;
  `,
  spamText: css`
    margin-bottom: 0;
    color: #636363;
    font-size: 14px;
    margin-top: var(--cds-spacing-150);
    font-family: 'Source Sans Pro', Arial, sans-serif;
    text-align: center;
  `,
  boldEmail: css`
    font-weight: 700;
    font-family: 'Source Sans Pro', Arial, sans-serif;
  `,
};

class MagicLink extends React.Component<Props, State> {
  state = {
    magicLinkSent: false,
  };

  navigateToForgotPassword = () => {
    const { navigate } = this.props;

    navigate(AUTHENTICATION_MODAL_MODES.FORGOT);
  };

  resendLink = () => {
    const { email, magicLinkRedirect } = this.props;
    api.post('/api/magicLinks.v1/encode', { data: { email, redirectUrl: magicLinkRedirect } });
    this.setState({ magicLinkSent: true });
  };

  render() {
    const { className, email } = this.props;
    const { magicLinkSent } = this.state;

    return (
      <section className={className} css={styles.container} aria-live="polite">
        <header css={styles.section}>
          <h1 css={styles.title}>{_t('Check your inbox')}</h1>
        </header>
        <p css={[styles.description, styles.section]}>
          <FormattedMessage
            message={_t('We emailed you an login link to \n {email}. \n The link will expire shortly.')}
            email={<span css={styles.boldEmail}>{email}</span>}
          />
        </p>
        <TrackedSubmit
          css={[styles.submit, styles.section, styles.buttonContainer]}
          trackingName="resend_login_link_button"
          disabled={magicLinkSent}
          onClick={this.resendLink}
          secondary
        >
          {_t('Re-send email')}
        </TrackedSubmit>
        <div css={styles.section}>
          <p css={styles.spamText}>{_t("Can't find your link? Check your spam folder.")}</p>

          <LinkButton css={styles.navigation} onClick={this.navigateToForgotPassword}>
            <div css={styles.navigationIcon}>
              <SvgArrowLeft color="#2A73CC" size={15} />
            </div>
            {_t('Go Back')}
          </LinkButton>
        </div>
      </section>
    );
  }
}

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'magic-link-confirm-page',
  },
}))(MagicLink);
